import { Icon, Card, withStyles, getLuminance } from 'vopay';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import useFetchUserRoles from '@/hooks/useApi/useFetchUserRoles';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import styles from './ApprovalSettingsContainer.module.scss';
import { useBranding } from '@/providers/Branding.provider';
import { useTranslation, Trans } from 'react-i18next';
import { Button, Title, BodyText, Skeleton } from 'UI';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

const cn = withStyles(styles);

type ApprovalSettingsContainerProps = {
  subAccountToken?: string;
  onNavigateToWorkflows?: () => void;
  onNavigateToWorkflowAdd?: () => void;
  onNavigateToRoles?: () => void;
};

const ApprovalSettingsContainer: FunctionComponent<ApprovalSettingsContainerProps> = ({
  subAccountToken,
  onNavigateToWorkflows,
  onNavigateToWorkflowAdd,
  onNavigateToRoles,
}) => {
  const { response: userRolesResponse, fetchUserRoles } = useFetchUserRoles({ bearer: subAccountToken });
  const { workflows, fetchWorkflows, fetchingWorkflows } = useFetchWorkflows({ bearer: subAccountToken });
  const [fetchAdmin, setFetchAdmin] = useState(true);
  const { hasInvalidWorkflows: parentHasInvalidWorkflows } = useApprovalWorkflow();
  const hasInvalidWorkflows = subAccountToken ? false : parentHasInvalidWorkflows;
  const { primaryColor } = useBranding();
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const alertDialog = useAlertDialog();

  useEffect(() => {
    fetchUserRoles({
      Role: 'super admin',
    });

    refreshWorkflows();
  }, []);

  useEffect(() => {
    if (userRolesResponse) {
      if (fetchAdmin) {
        fetchUserRoles({
          Role: 'approver',
        });
        setFetchAdmin(false);
      } else {
      }
    }
  }, [userRolesResponse]);

  const activeTransactionTypeIDs = useMemo(() => {
    return workflows.filter((group) => group.Workflows.some((wf) => wf.IsActive)).map((group) => group.TransactionType);
  }, [workflows]);

  async function refreshWorkflows() {
    try {
      await fetchWorkflows();
    } catch (err) {
      alertDialog.error(tGlobal('error'), tGlobal('errorOccurred'));
    }
  }

  const handleManageWorkflows = () => {
    if (onNavigateToWorkflows || onNavigateToWorkflowAdd) {
      workflows.length > 0 ? onNavigateToWorkflows?.() : onNavigateToWorkflowAdd?.();
    } else {
      history.push(
        workflows.length > 0
          ? '/transaction-approval/configuration/workflows'
          : '/transaction-approval/configuration/workflows/add',
      );
    }
  };

  const handleManageRoles = () => {
    if (onNavigateToRoles) {
      onNavigateToRoles();
    } else {
      history.push('/transaction-approval/configuration/roles');
    }
  };

  return (
    <div className={cn('approvalSettingsContainer')}>
      {!subAccountToken && <Title>{t('title')}</Title>}

      <Card className={cn('card')}>
        <div className={cn('cardText')}>
          <Title level="4" className={cn('cardTitle')}>
            <span>{t('Cards.Workflows.title', { defaultValue: 'Approval Workflow' })}</span>

            {hasInvalidWorkflows && (
              <div className={styles.notificationIconLarge}>
                <Icon
                  icon="exclamation"
                  width="4px"
                  height="10px"
                  color={getLuminance(primaryColor) > 0.5 ? 'primary-dark' : 'primary-light'}
                  position="center"
                />
              </div>
            )}
          </Title>

          <BodyText className={cn('cardDesc')}>
            {t('Cards.Workflows.desc', { defaultValue: 'Define approval rules for each payment railz' })}
          </BodyText>
        </div>

        {fetchingWorkflows ? (
          <div
            style={{
              height: '36px',
              width: '10%',
            }}
          >
            <Skeleton height="100%" />
          </div>
        ) : (
          <Button colorScheme="primary" onClick={handleManageWorkflows}>
            {t('Cards.Workflows.button', { defaultValue: 'Manage' })}
          </Button>
        )}
      </Card>

      <Card className={cn('card')}>
        <div className={cn('cardText')}>
          <Title level="4">{t('Cards.Roles.title', { defaultValue: 'Roles' })}</Title>

          <BodyText className={cn('cardDesc')}>
            {t('Cards.Roles.desc', {
              defaultValue: 'Assign users who can approve payments based on configured workflows.',
            })}
          </BodyText>

          {fetchingWorkflows ? null : workflows.length === 0 ? (
            <BodyText weight="em">
              <Trans
                t={t}
                i18nKey="Cards.Roles.configureWorkflow"
                defaults="Please <Link>configure a workflow</Link> before assigning roles"
                components={{
                  Link: onNavigateToWorkflowAdd ? (
                    <span
                      onClick={onNavigateToWorkflowAdd}
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    />
                  ) : (
                    <Link to="/transaction-approval/configuration/workflows/add" />
                  ),
                }}
              />
            </BodyText>
          ) : activeTransactionTypeIDs.length === 0 ? (
            <BodyText weight="em">
              <Trans
                t={t}
                i18nKey="Cards.Roles.enableWorkflow"
                defaults="Please <Link>enable a workflow</Link> before assigning roles"
                components={{
                  Link: onNavigateToWorkflows ? (
                    <span onClick={onNavigateToWorkflows} style={{ cursor: 'pointer', textDecoration: 'underline' }} />
                  ) : (
                    <Link to="/transaction-approval/configuration/workflows" />
                  ),
                }}
              />
            </BodyText>
          ) : null}
        </div>

        {fetchingWorkflows ? (
          <div
            style={{
              height: '36px',
              width: '10%',
            }}
          >
            <Skeleton height="100%" />
          </div>
        ) : (
          <Button
            colorScheme="primary"
            disabled={activeTransactionTypeIDs.length === 0 || workflows.length === 0}
            onClick={handleManageRoles}
          >
            {t('Cards.Roles.button', { defaultValue: 'Manage' })}
          </Button>
        )}
      </Card>
    </div>
  );
};
export default ApprovalSettingsContainer;
